import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import intToTime12 from "../../../navigo-module/helper/intToTime12";
import { getPatientAge, getPatientGender } from "../../../risk-stratification/utils/riskUtils";
import { CALL_OUTCOME_LABEL_MAP } from "../../constants";
import LTRService from "../../service/LTRService";
import { talkTime, nameOf } from "./CallAudit";

const api = new APIService();
const ltrService = new LTRService();

// Bridge's booking calls, exactly as its Create Appointment screen makes them
// (navigo-module/components/CreateAppointment.js).
const get = (url) => new Promise((resolve, reject) => api.get(url, resolve, reject));
const post = (url, body) => new Promise((resolve, reject) => api.post(url, body, resolve, reject));
// Create goes through postAgain: post() silently re-sends on error, which
// would book the patient twice.
const postOnce = (url, body) =>
  new Promise((resolve, reject) => api.postAgain(url, body, resolve, reject));

const SERVICE_TYPE_CONSULTATION = 1;
const REASON_OTHER = 6;
const REASON_FOLLOW_UP = 5;

const minutesOfDay = (m) => m.hours() * 60 + m.minutes();

function Field({ label, required, children, hint, error }) {
  return (
    <div className="ltr-form-field">
      <label className="ltr-field-label inline">
        {label}
        {required && <span className="ltr-required">*</span>}
      </label>
      {children}
      {(error || hint) && <p className={`ltr-field-hint${error ? " error" : ""}`}>{error || hint}</p>}
    </div>
  );
}

function Pick({ options, value, onChange, getLabel, placeholder, loading, disabled }) {
  return (
    <Autocomplete
      options={options || []}
      value={value}
      loading={loading}
      disabled={disabled}
      onChange={(e, v) => onChange(v)}
      getOptionLabel={(o) => (o ? getLabel(o) : "")}
      getOptionSelected={(o, v) => getLabel(o) === getLabel(v)}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" size="small" placeholder={placeholder} />
      )}
    />
  );
}

/**
 * Convert this call into an appointment (board 04.1).
 *
 * Books the consultation through api-appointment-service the way Bridge's own
 * Create Appointment screen does — plan → mode → service → provider → slot →
 * reason → create — dated from the call itself, then hands the booking back
 * through onBooked so the case can record it.
 *
 * The plan, mode and service are the LTR service's booking defaults, selected
 * and locked; when one is missing nothing is selected and the booking cannot
 * go ahead. A doctor's own provider is preselected, and stays changeable.
 */
function ConvertCallDialog({ open, caseRecord, call, patient, names, onClose, onBooked }) {
  // null while loading, { failed: true } when the request failed.
  const [defaults, setDefaults] = useState(null);
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState(null);
  const [services, setServices] = useState(null);
  const [mode, setMode] = useState(null);
  const [service, setService] = useState(null);
  const [providers, setProviders] = useState(null);
  const [provider, setProvider] = useState(null);
  const [reasons, setReasons] = useState(null);
  const [reason, setReason] = useState(null);
  const [reasonDetails, setReasonDetails] = useState("");
  const [slot, setSlot] = useState(null); // { slot, response } | { none: true }
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const patientId = caseRecord?.patientId;
  const callStart = call?.startedAt ? moment(call.startedAt) : moment();
  const callEnd = call?.endedAt ? moment(call.endedAt) : null;
  const consultDate = callStart.format("YYYY-MM-DD");

  // ---- load plans and reasons when opened ----------------------------------
  useEffect(() => {
    if (!open || !patientId) return;
    setError("");
    setDefaults(null);
    setPlans(null);
    setPlan(null);
    setServices(null);
    setMode(null);
    setService(null);
    setProviders(null);
    setProvider(null);
    setSlot(null);
    setReasonDetails(
      `LTR follow-up${
        caseRecord?.riskLevelTriggeredBy?.length ? ` — ${caseRecord.riskLevelTriggeredBy.join(", ")}` : ""
      }`,
    );

    ltrService.fetchBookingDefaults(
      (res) => setDefaults(res || { failed: true }),
      (err) => {
        console.error("Error fetching LTR booking defaults:", err);
        setDefaults({ failed: true });
      },
    );

    get(APIConfig.myPlan.navigo.getPlansForPid.format(patientId))
      .then((res) => setPlans((Array.isArray(res) ? res : []).filter((p) => p.bookConsultation)))
      .catch((err) => {
        setPlans([]);
        setError(err?.message || "Could not load the patient's plans.");
      });

    get(APIConfig.myPlan.navigo.reasonsForConsultation)
      .then((res) => {
        const list = (Array.isArray(res) ? res : []).filter((r) => r && r.isActive);
        setReasons(list);
        setReason(list.find((r) => r.id === REASON_FOLLOW_UP) || null);
      })
      .catch(() => setReasons([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  // ---- the LTR plan, on the DC account ---------------------------------------
  // Only that plan: a patient the programme assignment missed has no plan to
  // book on here, and is sent to the case's Retry instead.
  useEffect(() => {
    if (!open || !plans || !defaults) return;
    setPlan(
      plans.find(
        (p) => Number(p.planId) === Number(defaults.planId) && p.accountCode === defaults.accountCode,
      ) || null,
    );
  }, [open, plans, defaults]);

  // ---- services for the plan -----------------------------------------------
  useEffect(() => {
    if (!open || !plan) return;
    setServices(null);
    setMode(null);
    setService(null);
    post(APIConfig.myPlan.navigo.getServices, {
      pageNum: 0,
      pageSize: 0,
      planId: plan.planId,
      serviceType: SERVICE_TYPE_CONSULTATION,
      patientId,
      memberPatientId: patientId,
      includeSubtypeIds: [],
      excludeSubtypeIds: [],
    })
      .then((res) => setServices(res?.services ?? []))
      .catch((err) => {
        setServices([]);
        setError(err?.message || "Could not load services for this plan.");
      });
  }, [open, plan, patientId]);

  // Modes across the plan's services, cheapest price first.
  const modes = useMemo(() => {
    const byName = {};
    (services || []).forEach((s) =>
      (s.serviceModeDetails || []).forEach((m) => {
        const price = Number(m.discountedPrice) || 0;
        if (!(m.modeName in byName) || price < byName[m.modeName]) byName[m.modeName] = price;
      }),
    );
    return Object.keys(byName).map((name) => ({ name, price: byName[name] }));
  }, [services]);

  // The LTR mode and service, once the plan's services are in.
  useEffect(() => {
    if (mode || !modes.length || !defaults || !defaults.serviceMode) return;
    const want = defaults.serviceMode.trim().toLowerCase();
    const match = modes.find((m) => m.name.trim().toLowerCase() === want);
    if (match) setMode(match);
  }, [modes, mode, defaults]);

  const servicesForMode = useMemo(
    () =>
      (services || []).filter((s) =>
        (s.serviceModeDetails || []).some((m) => mode && m.modeName === mode.name),
      ),
    [services, mode],
  );

  useEffect(() => {
    if (service || !defaults || !defaults.serviceId) return;
    const match = servicesForMode.find((s) => Number(s.serviceId) === Number(defaults.serviceId));
    if (match) setService(match);
  }, [servicesForMode, service, defaults]);

  // ---- providers for service + mode ----------------------------------------
  useEffect(() => {
    if (!open || !plan || !service || !mode) return;
    setProviders(null);
    setProvider(null);
    setSlot(null);
    post(APIConfig.myPlan.navigo.getServiceProvider, {
      pageNum: 0,
      pageSize: 0,
      planId: plan.planId,
      serviceId: service.serviceId,
      serviceMode: mode.name,
      patientId,
      memberPatientId: patientId,
      languageMatch: [],
      locationFilter: [],
      geolocationFilter: null,
    })
      .then((res) => setProviders(res?.providers ?? []))
      .catch((err) => {
        setProviders([]);
        setError(err?.message || "Could not load providers.");
      });
  }, [open, plan, service, mode, patientId]);

  // A doctor's own provider, preselected. The plan's provider list has no
  // doctor id, so the providers' own records are read for it: a consultation
  // provider names its doctor by profile id. Left for the user to pick when
  // the doctor is not among them or the read fails.
  useEffect(() => {
    if (!open || !providers || !providers.length || !defaults || !(defaults.doctorId > 0)) return undefined;
    let cancelled = false;
    post(APIConfig.myPlan.serviceProviderDashboard.fetchServiceProvidersByFilter, {
      pageNum: 1,
      pageSize: providers.length,
      Ids: providers.map((p) => p.id),
    })
      .then((res) => {
        if (cancelled) return;
        const mine = (res?.providers || []).find(
          (p) => Number(p?.consultationInfo?.identityDetails?.doctorId) === Number(defaults.doctorId),
        );
        const option = mine && providers.find((p) => p.id === mine.id);
        if (option) setProvider((current) => current || option);
      })
      .catch((err) => console.error("Error matching the doctor to a provider:", err));
    return () => {
      cancelled = true;
    };
  }, [open, providers, defaults]);

  // ---- the slot the call falls in ------------------------------------------
  useEffect(() => {
    if (!open || !plan || !service || !mode || !provider) return;
    setSlot(null);
    post(APIConfig.myPlan.navigo.getAvailableSlots, {
      planId: plan.planId,
      serviceId: service.serviceId,
      patientId,
      serviceProviderId: provider.id,
      bookingMode: mode.name,
      collectionDate: consultDate,
      addressId: null,
      vendor: provider.vendorId ?? "",
      vendorCode: provider.vendorCode ?? "",
      vendorDoctorId: provider.vendorDoctorId ?? 0,
      providerAddressId: provider.address?.providerAddressId ?? "",
      locationFilter: [],
      languageMatch: [],
      memberPatientId: patientId,
    })
      .then((res) => {
        const slots = (res && Array.isArray(res.slots) ? res.slots : []).slice();
        if (!slots.length) {
          setSlot({ none: true });
          return;
        }
        slots.sort((a, b) => a.value - b.value);
        // The slot the call started in, else the next one that day, else
        // the last one.
        const at = minutesOfDay(callStart);
        const containing = slots.filter((s) => s.value <= at).pop();
        const chosen = containing || slots.find((s) => s.value >= at) || slots[slots.length - 1];
        setSlot({ slot: chosen, response: res });
      })
      .catch((err) => {
        setSlot({ none: true });
        setError(err?.message || "Could not check the provider's availability.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan, service, mode, provider, consultDate, patientId]);

  if (!open || !caseRecord || !call) return null;

  const price = provider ? Number(provider.price) || 0 : 0;

  // Why a locked field is empty. Worked out from what is on offer rather than
  // from the selection, which lands an effect later.
  const defaultsReady = !!defaults && !defaults.failed;
  const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  let planError = "";
  if (defaults && defaults.failed) planError = "Could not load the LTR booking settings. Close this and try again.";
  else if (
    defaultsReady &&
    plans &&
    !plans.some((p) => Number(p.planId) === Number(defaults.planId) && p.accountCode === defaults.accountCode)
  )
    planError = `The patient is not on the LTR plan (${defaults.planId} on ${defaults.accountCode}). Retry the programme assignment on the case, then reopen this.`;
  const modeError =
    defaultsReady && plan && services !== null && !modes.some((m) => sameName(m.name, defaults.serviceMode))
      ? `${defaults.serviceMode} is not offered on the LTR plan.`
      : "";
  let serviceError = "";
  if (defaultsReady && !defaults.serviceId) serviceError = "No LTR consultation service is set up for this environment.";
  else if (
    defaultsReady &&
    mode &&
    services !== null &&
    !servicesForMode.some((s) => Number(s.serviceId) === Number(defaults.serviceId))
  )
    serviceError = `Service ${defaults.serviceId} is not available on the LTR plan for ${mode.name}.`;
  const reasonNeedsDetails = reason && reason.id === REASON_OTHER;
  const canSubmit =
    !!plan &&
    !!mode &&
    !!service &&
    !!provider &&
    slot &&
    slot.slot &&
    price === 0 &&
    !!reason &&
    (!reasonNeedsDetails || !!reasonDetails.trim()) &&
    !saving;

  const patientName = patient ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() : "";
  const age = getPatientAge(patient);
  const identity = [
    patientName || null,
    [getPatientGender(patient), age != null ? age : null].filter((x) => x !== "" && x != null).join(", ") || null,
    `PID ${patientId}`,
    caseRecord.ltrId,
  ]
    .filter(Boolean)
    .join(" · ");

  const submit = () => {
    if (!canSubmit || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    const res = slot.response || {};
    const reasonLabel = reasonNeedsDetails ? reasonDetails.trim() : reason.name;
    const body = {
      appointmentDate: `${consultDate}T00:00:00.000Z`,
      bookedFor: 0,
      patientID: patientId,
      serviceID: service.serviceId,
      timeSlot: slot.slot.value,
      timeSlotId: slot.slot.key,
      planID: plan.planId,
      mode: mode.name,
      serviceProviderID: provider.id,
      isCustomContactNumber: false,
      customContactNumber: { countryCode: 0, phoneNo: 0 },
      reportURL: "",
      reasonId: reason.id,
      reasonDetails: reasonDetails.trim(),
      // Echoed from the slot response, as Create Appointment does (note its
      // vendorName → vendorCode mapping).
      vendorId: res.vendorId ?? "",
      vendorCode: res.vendorName ?? "",
      vendorDoctorId: res.vendorDoctorId ?? 0,
      providerAddressId: res.providerAddressId ?? "",
    };
    postOnce(APIConfig.myPlan.navigo.createAppointment, body)
      .then((appointment) => {
        if (!appointment || !appointment.id) throw new Error("The appointment service did not return an id.");
        return onBooked({
          appointment,
          link: {
            appointmentId: appointment.id,
            callId: call.callId,
            status: typeof appointment.status === "number" ? appointment.status : 0,
            consultationAt: call.startedAt,
            mode: mode.name,
            serviceName: service.name,
            providerName: `${provider.name}${provider.shortdescription ? ` — ${provider.shortdescription}` : ""}`,
            reason: reasonLabel,
          },
        });
      })
      .catch((err) => {
        console.error("Error converting call to appointment:", err);
        setError(err?.message || "Could not create the appointment. Please try again.");
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  const timeLabel = `${callStart.format("h:mm")}${callEnd ? ` – ${callEnd.format("h:mm A")}` : ` ${callStart.format("A")}`}`;

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "ltr-modal" }}
    >
      <div className="ltr-modal-header">
        <h3>Convert this call into an appointment</h3>
        <button type="button" className="ltr-modal-close" onClick={() => !saving && onClose()} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="ltr-modal-body">
        <div className="ltr-convert-context">
          <div className="ltr-strong">{identity}</div>
          {plan && (
            <div className="ltr-cell-sub">
              {plan.accountName ? `${plan.accountName} (${plan.accountCode})` : plan.accountCode} · {plan.name} (
              {plan.planId})
            </div>
          )}
          <div className="ltr-convert-divider" />
          <div className="ltr-convert-call">
            <div>
              <div className="ltr-strong">
                From the call — {nameOf(names, call.callerId)} · {callStart.format("D MMM YYYY, h:mm A")} ·{" "}
                {(CALL_OUTCOME_LABEL_MAP[call.outcome] || call.outcome || "").toLowerCase()}
                {call.durationSeconds ? ` · ${talkTime(call.durationSeconds)}` : ""}
              </div>
              {call.remark && <div className="ltr-cell-sub">Call note — {call.remark}</div>}
            </div>
          </div>
        </div>

        <Field label="Plan" required error={planError}>
          <Pick
            options={plans}
            value={plan}
            onChange={setPlan}
            disabled
            getLabel={(p) => `(${p.planId}) ${p.name}${p.accountCode ? ` · ${p.accountCode}` : ""}`}
            placeholder={plans === null || defaults === null ? "Loading the LTR plan..." : "No LTR plan"}
          />
        </Field>

        <h4 className="ltr-form-section">Service &amp; provider</h4>
        <div className="ltr-field-grid">
          <Field label="Service mode" required error={modeError}>
            <Pick
              options={modes}
              value={mode}
              onChange={setMode}
              disabled
              getLabel={(m) => `${m.name} (Rs ${m.price})`}
              placeholder={plan && services === null ? "Loading..." : "No mode"}
            />
          </Field>
          <Field label="Service code / name" required error={serviceError}>
            <Pick
              options={servicesForMode}
              value={service}
              onChange={setService}
              disabled
              getLabel={(s) => `(${s.serviceId}) ${s.name}${mode ? ` (${mode.name})` : ""}`}
              placeholder={plan && services === null ? "Loading..." : "No service"}
            />
          </Field>
        </div>
        <Field
          label="Service provider"
          required
          hint={
            provider && price > 0
              ? `This consultation costs Rs ${price} for this patient. Chargeable bookings need a payment link — book them from the patient's Create appointment screen.`
              : null
          }
        >
          <Pick
            options={providers}
            value={provider}
            onChange={setProvider}
            disabled={!service}
            loading={!!service && providers === null}
            getLabel={(p) => `(${p.id}) ${p.name}${p.shortdescription ? ` — ${p.shortdescription}` : ""}`}
            placeholder="Select a provider"
          />
        </Field>

        <h4 className="ltr-form-section">
          Consultation record <span className="ltr-soft-tag">from the call</span>
        </h4>
        <div className="ltr-field-grid">
          <Field label="Consultation date">
            <input className="ltr-input full readonly" readOnly value={callStart.format("D MMM YYYY")} />
          </Field>
          <Field
            label="Time"
            hint={
              slot && slot.slot
                ? `Booked in the provider's ${intToTime12(slot.slot.value)} slot`
                : slot && slot.none
                ? "This provider has no open slot that day — pick another provider."
                : null
            }
          >
            <input className="ltr-input full readonly" readOnly value={timeLabel} />
          </Field>
        </div>

        <Field label="Reason for consultation" required>
          <Pick
            options={reasons}
            value={reason}
            onChange={setReason}
            loading={reasons === null}
            getLabel={(r) => r.name}
            placeholder="Select a reason"
          />
        </Field>
        <Field label={reasonNeedsDetails ? "Describe the reason" : "Details"} required={reasonNeedsDetails}>
          <input
            className="ltr-input full"
            value={reasonDetails}
            maxLength={200}
            onChange={(e) => setReasonDetails(e.target.value)}
          />
        </Field>
      </div>
      <div className="ltr-modal-footer">
        {error && <span className="ltr-modal-error">{error}</span>}
        <button type="button" className="ltr-btn" onClick={() => !saving && onClose()} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="ltr-btn purple" onClick={submit} disabled={!canSubmit}>
          {saving ? "Converting..." : "Convert to appointment"}
          {!saving && <FontIcon>arrow_forward</FontIcon>}
        </button>
      </div>
    </Dialog>
  );
}

// Call recorded as an appointment (board 04.2) — offers the Rx straight away.
export function AppointmentCreatedDialog({ open, appointment, onClose, onCreateRx }) {
  if (!appointment) return null;
  const consultation = appointment.consultationAt
    ? moment(appointment.consultationAt).format("D MMM YYYY · h:mm A")
    : "-";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: "ltr-modal" }}>
      <div className="ltr-modal-header">
        <h3 className="ltr-modal-title-check">
          <span className="ltr-check-badge">
            <FontIcon>check</FontIcon>
          </span>
          Call recorded as an appointment
        </h3>
      </div>
      <div className="ltr-modal-body">
        <div className="ltr-prompt-bar">Write a prescription for this consultation?</div>
        <div className="ltr-summary-table">
          {[
            ["Appointment ID", appointment.appointmentId],
            ["Consultation on", consultation],
            ["Mode", [appointment.mode, appointment.serviceName].filter(Boolean).join(" · ")],
            ["Provider", appointment.providerName],
            ["Reason", appointment.reason],
          ]
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="ltr-summary-row">
                <span>{k}</span>
                <strong>{v}</strong>
              </div>
            ))}
        </div>
      </div>
      <div className="ltr-modal-footer">
        <button type="button" className="ltr-btn" onClick={onClose}>
          Not now
        </button>
        <button type="button" className="ltr-btn purple" onClick={onCreateRx}>
          Create Rx
          <FontIcon>arrow_forward</FontIcon>
        </button>
      </div>
    </Dialog>
  );
}

export default ConvertCallDialog;
